import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import QRCodeModal from "../modals/QRCodeModal";

const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.vettconsult.mobile&pcampaignid=web_share";
const IOS_URL = "https://apps.apple.com/es/app/vettconsult/id6756122991";

const AppDownloadSection: React.FC = () => {
  const { t } = useTranslation();
  const [showQRModal, setShowQRModal] = useState(false);
  const [initialPlatform, setInitialPlatform] = useState<"ios" | "android">(
    "ios",
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const openModal = (platform: "ios" | "android") => {
    setInitialPlatform(platform);
    setShowQRModal(true);
  };

  return (
    <section className="relative w-full py-20 overflow-hidden bg-gradient-to-b from-[#00342d] to-[#002621]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full max-w-3xl text-white"
          >
            <h2
              className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
              style={{ fontFamily: "'Bruno Ace', sans-serif" }}
            >
              {t("appDownload.title", "Download Our Mobile App")}
            </h2>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              {t(
                "appDownload.subtitle",
                "Access your veterinary dashboard, appointments, and consultations from anywhere. Available now for iOS and Android.",
              )}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              {/* Custom Styled Buttons mimicking Store Badges */}

              {isMobile ? (
                // MOBILE: Direct Links
                <a
                  href={IOS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-white text-black px-8 py-3.5 rounded-xl transition-all duration-300 hover:scale-105 hover:bg-gray-100 min-w-[200px]"
                >
                  <svg
                    viewBox="0 0 384 512"
                    fill="currentColor"
                    className="h-8 w-8"
                  >
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-54.5-92-35.4-121.9zm-50.5-141c25.3-37.6 20.8-93.5-1.2-127.3-25.3 4.1-61.9 28.3-80.4 69.3-16.7 34.6-25.5 83.3 1.2 119.2 28.5 2.5 59.5-24.6 80.4-61.2z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="text-xl font-bold leading-none">
                      App Store
                    </div>
                  </div>
                </a>
              ) : (
                // DESKTOP: Modal Trigger
                <button
                  onClick={() => openModal("ios")}
                  className="flex items-center justify-center gap-3 bg-white text-black px-8 py-3.5 rounded-xl transition-all duration-300 hover:scale-105 hover:bg-gray-100 min-w-[200px]"
                >
                  <svg
                    viewBox="0 0 384 512"
                    fill="currentColor"
                    className="h-8 w-8"
                  >
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-54.5-92-35.4-121.9zm-50.5-141c25.3-37.6 20.8-93.5-1.2-127.3-25.3 4.1-61.9 28.3-80.4 69.3-16.7 34.6-25.5 83.3 1.2 119.2 28.5 2.5 59.5-24.6 80.4-61.2z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="text-xl font-bold leading-none">
                      App Store
                    </div>
                  </div>
                </button>
              )}

              {isMobile ? (
                // MOBILE: Direct Links
                <a
                  href={ANDROID_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-transparent border-2 border-white/30 text-white px-8 py-3.5 rounded-xl transition-all duration-300 hover:bg-white/10 hover:border-white min-w-[200px]"
                >
                  <svg
                    viewBox="0 0 512 512"
                    fill="currentColor"
                    className="h-8 w-8"
                  >
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L47 499z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">GET IT ON</div>
                    <div className="text-xl font-bold leading-none">
                      Google Play
                    </div>
                  </div>
                </a>
              ) : (
                // DESKTOP: Modal Trigger
                <button
                  onClick={() => openModal("android")}
                  className="flex items-center justify-center gap-3 bg-transparent border-2 border-white/30 text-white px-8 py-3.5 rounded-xl transition-all duration-300 hover:bg-white/10 hover:border-white min-w-[200px]"
                >
                  <svg
                    viewBox="0 0 512 512"
                    fill="currentColor"
                    className="h-8 w-8"
                  >
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L47 499z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">GET IT ON</div>
                    <div className="text-xl font-bold leading-none">
                      Google Play
                    </div>
                  </div>
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        initialPlatform={initialPlatform}
      />
    </section>
  );
};

export default AppDownloadSection;
